import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  SelectControlValueAccessor,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConversationChannel } from '@vendasta/conversation';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Validator } from '@vendasta/shared';
import { parsePhoneNumber } from 'libphonenumber-js';
import { debounceTime, Observable, of, shareReplay, skip, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { InboxContact, InboxContactInfo, CreateContactState } from '../../../../core/src/lib/interface/inbox.interface';
import { ParticipantService } from '../../../../core/src/lib/participant.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_ROUTES_TOKEN,
  PARTNER_ID_TOKEN,
} from '../../../../core/src/lib/tokens';
import { formatPhoneNumber } from '../../../../core/src/lib/phone-number-utils';

export class SelectContactControl extends FormControl<InboxContact | string> {
  constructor(value?: InboxContact | string, validator?: ValidatorFn | ValidatorFn[] | null) {
    const validators = validator ? [controlValidator(), validator].flat() : [controlValidator()];
    super(value, validators);
  }
}

function isPhoneNumberAttempt(value: string): boolean {
  return !isNaN(Number(value.replace(/[-+)(\s]/g, '')));
}

function isEmailAttempt(value: string): boolean {
  return /@.*\./.test(value);
}

@Component({
  selector: 'inbox-select-contact',
  templateUrl: './inbox-select-contact.component.html',
  styleUrls: ['./inbox-select-contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: SelectControlValueAccessor,
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    GalaxyFormFieldModule,
    GalaxyEmptyStateModule,
  ],
})
export class InboxSelectContactComponent implements OnDestroy, OnInit {
  control?: SelectContactControl;
  private readonly inboxService = inject(InboxService);
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly unsubscribe$ = new Subject<void>();
  @ViewChild(MatAutocompleteTrigger) trigger?: MatAutocompleteTrigger;

  contacts$?: Observable<InboxContact[]>;

  private readonly ownerIdToken = this.inboxService.isPartnerCenter ? PARTNER_ID_TOKEN : ACCOUNT_GROUP_ID_TOKEN;
  private readonly ownerId = toSignal(inject(this.ownerIdToken), { requireSync: true });

  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));
  private selectedOption?: string | InboxContact;
  @Output() suggestedChannel = new EventEmitter<ConversationChannel>();

  constructor(
    private router: Router,
    private participantService: ParticipantService,
    @Self() readonly ngControl: NgControl,
  ) {}

  ngOnInit(): void {
    if (!this.ngControl || !(this.ngControl.control instanceof SelectContactControl)) {
      throw new Error('CustomInputComponent: No ControlValueAccessor provided');
    }
    this.control = this.ngControl.control;

    this.contacts$ = this.control.valueChanges.pipe(
      debounceTime(200),
      startWith(''),
      takeUntil(this.unsubscribe$),
      switchMap((term) => {
        if (typeof term === 'object' && term.contactId) {
          return of([] as InboxContact[]);
        }
        const searchTerm = this.getSearchTerm(term);

        this.participantService.searchContacts(searchTerm ?? '', this.ownerId());
        // skip the first value because it always starts as []
        return this.participantService.contacts$.pipe(skip(1));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  protected onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.id === 'add-contact') {
      this.openAddContactForm();
      return;
    }
    this.selectedOption = event.option.value;
  }

  async searchChanged(): Promise<void> {
    const contact = this.control?.value;
    if (typeof contact !== 'string' || !this.control) return;

    try {
      if (Validator.isEmail(this.control) === null && contact.length) {
        this.control.setValue({ email: contact });
        return;
      }

      const parsed = parsePhoneNumber(contact, 'US');
      if (parsed.isValid()) this.control.setValue({ phone: parsed.number });
    } catch {
      return;
    }
  }

  openAddContactForm(): void {
    const routes = this.routes();
    if (!this.control || !routes) return;

    const searchTerm = this.control.value;
    const state = this.buildNavigationState(searchTerm);

    this.hostAppInterface.navigateToCreateContact(state, routes);
  }

  private buildNavigationState(searchTerm: string | InboxContact): CreateContactState {
    if (typeof searchTerm === 'string' && isPhoneNumberAttempt(searchTerm)) {
      return { phone: searchTerm };
    }
    if (typeof searchTerm === 'string' && isEmailAttempt(searchTerm)) {
      return { email: searchTerm };
    }
    return typeof searchTerm === 'object' ? { ...searchTerm } : { firstName: searchTerm };
  }

  displayContactInfo(contact: InboxContact): InboxContactInfo | null {
    if (!contact) return null;

    const phone = contact?.phone ? formatPhoneNumber(contact.phone) : undefined;
    const email = contact?.email;

    return {
      name: [contact.firstName, contact.lastName].filter((s) => !!s).join(' '),
      phoneAndEmail: [phone, email].filter((s) => !!s).join('  '),
    };
  }

  displayContact(contact: InboxContact): string {
    if (!contact) return '';

    const phone = contact?.phone ? formatPhoneNumber(contact.phone) : undefined;
    const email = contact?.email;

    return [contact.firstName, contact.lastName, phone, email].filter((s) => !!s).join('  ');
  }

  getPhoneSearchTerm(term: string): string {
    const cleanTerm = term.replace(/[-+)(\s]/g, '').trim();

    if (cleanTerm.startsWith('1')) return '+' + cleanTerm;

    return '+1' + cleanTerm;
  }

  getSearchTerm(term: string | InboxContact): string {
    const emailControlValue = { value: term } as AbstractControl<any, string>;
    const phoneControlValue = { value: term } as UntypedFormControl;

    switch (typeof term) {
      case 'object':
        if (term?.phone) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_SMS);
          return term?.phone;
        }
        if (term?.email) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_EMAIL);
          return term?.email;
        }
        return '';
      case 'string':
        if (Validator.isPhoneNumber(phoneControlValue) === null && term.length) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_SMS);
          return this.getPhoneSearchTerm(term);
        }
        if (Validator.isEmail(emailControlValue) === null && term.length) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_EMAIL);
          return term;
        }
        this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED);
        return term;
      default:
        this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED);
        return term;
    }
  }
}

function controlValidator(): ValidatorFn {
  return (control: AbstractControl<InboxContact | string>): ValidationErrors | null => {
    const { value: contact } = control;

    if (!control.dirty && !control.touched) {
      return null;
    }

    if (!contact) return { invalidSelection: true };

    if (typeof contact === 'string') {
      let validPhoneNumber: boolean;
      try {
        //TODO: MEGA-1093 - use CRM shared validator instead of external library
        const parsedPhoneNumber = parsePhoneNumber(contact, 'US');
        validPhoneNumber = parsedPhoneNumber.isValid();
      } catch {
        validPhoneNumber = false;
      }

      const validEmail = Validator.isEmail(control) === null;
      return validPhoneNumber || validEmail ? null : { invalidInput: true };
    }

    return null;
  };
}
